/**
 * Admin TMA API Client
 * Handles communication with backend Admin API
 * Requirements: 16.1
 */

const API_BASE_URL = '/api/admin';

/**
 * Get Telegram initData from WebApp for authentication
 */
function getTelegramInitData(): string | undefined {
  const tg = window.Telegram?.WebApp;
  const initData = tg?.initData;
  return typeof initData === 'string' && initData.length > 0 ? initData : undefined;
}

function getDevTelegramId(): string | undefined {
  if (!import.meta.env.DEV) {
    return undefined;
  }

  const devId = import.meta.env.VITE_DEV_TELEGRAM_ID;
  return typeof devId === 'string' && devId.length > 0 ? devId : undefined;
}

/**
 * Base fetch wrapper with authentication
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const initData = getTelegramInitData();
  const devTelegramId = !initData ? getDevTelegramId() : undefined;
  if (!initData && !devTelegramId) {
    throw new Error('Missing Telegram initData');
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(initData ? { 'X-Telegram-Init-Data': initData } : {}),
      ...(devTelegramId ? { 'X-Dev-Telegram-Id': devTelegramId } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Types
export interface Document {
  id: string;
  type: string;
  verified: boolean;
  verifiedAt: string | null;
  createdAt: string;
}

export interface Master {
  id: string;
  telegramId: string;
  name: string;
  phone: string | null;
  masterStatus: MasterStatus;
  masterType: string | null;
  rating: number;
  commissionDebt: string | null;
  districts: string[];
  brands: string[];
  documents: Document[];
  createdAt: string;
  updatedAt: string;
}

export type MasterStatus = 
  | 'PENDING_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'ONLINE'
  | 'OFFLINE'
  | 'BANNED';

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ApiResponse {
  success: boolean;
  message: string;
  masterId?: string;
}

// API Functions

/**
 * Fetch masters with optional filters
 * - status: single status filter
 * - activeApproved: show APPROVED, ONLINE, OFFLINE masters
 * - onlyMyApproved: show only masters approved by current admin
 */
export async function fetchMasters(params: {
  status?: MasterStatus;
  activeApproved?: boolean;
  onlyMyApproved?: boolean;
  page?: number;
  limit?: number;
}): Promise<PaginatedResponse<Master>> {
  const searchParams = new URLSearchParams();

  if (params.status) {
    searchParams.set('status', params.status);
  }
  if (params.activeApproved) {
    searchParams.set('activeApproved', 'true');
  }
  if (params.onlyMyApproved) {
    searchParams.set('onlyMyApproved', 'true');
  }
  if (params.page) {
    searchParams.set('page', params.page.toString());
  }
  if (params.limit) {
    searchParams.set('limit', params.limit.toString());
  }

  const query = searchParams.toString();
  return apiFetch<PaginatedResponse<Master>>(`/masters${query ? `?${query}` : ''}`);
}

/**
 * Approve a master
 */
export async function approveMaster(masterId: string): Promise<ApiResponse> {
  return apiFetch<ApiResponse>(`/masters/${masterId}/approve`, {
    method: 'POST',
  });
}

/**
 * Reject a master with reason
 */
export async function rejectMaster(
  masterId: string,
  reason: string
): Promise<ApiResponse> {
  return apiFetch<ApiResponse>(`/masters/${masterId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/**
 * Get presigned URL for document preview
 * Note: This would need a backend endpoint to generate presigned URLs
 */
export async function getDocumentUrl(documentId: string): Promise<string> {
  // For now, return a placeholder - backend needs to implement this endpoint
  const response = await apiFetch<{ url: string }>(`/documents/${documentId}/url`);
  return response.url;
}

/**
 * Block a master
 * Requirements: 16.5
 */
export async function blockMaster(
  masterId: string,
  reason: string
): Promise<ApiResponse> {
  return apiFetch<ApiResponse>(`/masters/${masterId}/block`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/**
 * Unblock a master
 * Requirements: 16.5
 */
export async function unblockMaster(masterId: string): Promise<ApiResponse> {
  return apiFetch<ApiResponse>(`/masters/${masterId}/unblock`, {
    method: 'POST',
  });
}

// Order Types
export type OrderStatus =
  | 'BIDDING'
  | 'ASSIGNED'
  | 'IN_PROGRESS'
  | 'PAYMENT_PENDING'
  | 'PAID_CARD'
  | 'PAID_CASH'
  | 'COMPLETED'
  | 'CANCELED'
  | 'REFUNDED'
  | 'DISPUTED';

export type OrderType = 'VIDEO_CONSULT' | 'REPAIR' | 'MAINTENANCE';

export interface OrderClient {
  id: string;
  name: string;
  phone: string | null;
  telegramId: string;
}

export interface OrderMaster {
  id: string;
  name: string;
  phone: string | null;
  telegramId: string;
  rating: number;
}

export interface OrderBid {
  id: string;
  price: string;
  comment: string | null;
  createdAt: string;
  master: {
    id: string;
    name: string;
    rating: number;
  };
}

export interface Order {
  id: string;
  type: OrderType;
  status: OrderStatus;
  description: string;
  photos: string[];
  estimatedPrice: string | null;
  finalPrice: string | null;
  commissionAmount: string | null;
  paymentId: string | null;
  paymentMethod: string | null;
  client: OrderClient;
  master: OrderMaster | null;
  bids: OrderBid[];
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  // Dispute fields
  disputedAt: string | null;
  disputedBy: string | null;
  disputeReason: string | null;
  disputeComment: string | null;
  statusBeforeDispute: OrderStatus | null;
  resolvedAt: string | null;
  resolution: string | null;
  resolutionComment: string | null;
  refundAmount: string | null;
}

export type DisputeReason =
  | 'POOR_QUALITY' | 'OVERCHARGED' | 'DAMAGE_CAUSED' | 'MASTER_NO_SHOW'
  | 'CLIENT_NO_SHOW' | 'PAYMENT_ISSUE' | 'SAFETY_CONCERN' | 'OTHER';

export type DisputeResolution =
  | 'FULL_REFUND' | 'PARTIAL_REFUND' | 'COMPLETE_AS_IS' | 'CLOSE_NO_ACTION';

export interface DisputeContext {
  order: {
    id: string;
    orderNumber: number;
    status: OrderStatus;
    statusBeforeDispute: OrderStatus | null;
    finalPrice: string | null;
    paymentId: string | null;
    paymentMethod: string | null;
  };
  dispute: {
    disputedAt: string | null;
    disputedBy: string | null;
    disputeReason: string | null;
    disputeComment: string | null;
  };
  availableResolutions: DisputeResolution[];
  hasPayment: boolean;
  maxRefundAmount: string | null;
}

export interface OrderFilters {
  status?: OrderStatus;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

/**
 * Fetch orders with optional filters
 * Requirements: 16.2
 */
export async function fetchOrders(
  params: OrderFilters
): Promise<PaginatedResponse<Order>> {
  const searchParams = new URLSearchParams();

  if (params.status) {
    searchParams.set('status', params.status);
  }
  if (params.dateFrom) {
    searchParams.set('dateFrom', params.dateFrom);
  }
  if (params.dateTo) {
    searchParams.set('dateTo', params.dateTo);
  }
  if (params.page) {
    searchParams.set('page', params.page.toString());
  }
  if (params.limit) {
    searchParams.set('limit', params.limit.toString());
  }

  const query = searchParams.toString();
  return apiFetch<PaginatedResponse<Order>>(`/orders${query ? `?${query}` : ''}`);
}

/**
 * Assign a master to an order
 * Requirements: 16.3
 */
export async function assignMasterToOrder(
  orderId: string,
  masterId: string
): Promise<ApiResponse> {
  return apiFetch<ApiResponse>(`/orders/${orderId}/assign`, {
    method: 'POST',
    body: JSON.stringify({ masterId }),
  });
}

/**
 * Mark an order as disputed
 * Requirements: 16.3
 */
export async function disputeOrder(
  orderId: string,
  reason: DisputeReason,
  comment?: string,
): Promise<ApiResponse> {
  return apiFetch<ApiResponse>(`/orders/${orderId}/dispute`, {
    method: 'POST',
    body: JSON.stringify({ reason, comment }),
  });
}

export async function getDisputeContext(orderId: string): Promise<DisputeContext> {
  return apiFetch<DisputeContext>(`/orders/${orderId}/dispute-context`);
}

export async function resolveDispute(
  orderId: string,
  resolution: DisputeResolution,
  refundAmount?: number,
  comment?: string,
): Promise<ApiResponse> {
  return apiFetch<ApiResponse>(`/orders/${orderId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ resolution, refundAmount, comment }),
  });
}

// Finance Types
export type FinancePeriod = 'day' | 'week' | 'month';

export interface PaymentMethodStats {
  count: number;
  amount: string;
}

export interface FinanceSummary {
  period: FinancePeriod;
  startDate: string;
  endDate: string;
  summary: {
    totalRevenue: string;
    totalCommission: string;
    totalCommissionDebt: string;
    ordersByPaymentMethod: {
      card: PaymentMethodStats;
      cash: PaymentMethodStats;
    };
    completedOrders: {
      count: number;
      amount: string;
    };
  };
}

/**
 * Fetch financial summary by period
 * Requirements: 16.6
 */
export async function fetchFinanceSummary(params: {
  period?: FinancePeriod;
  date?: string;
}): Promise<FinanceSummary> {
  const searchParams = new URLSearchParams();

  if (params.period) {
    searchParams.set('period', params.period);
  }
  if (params.date) {
    searchParams.set('date', params.date);
  }

  const query = searchParams.toString();
  return apiFetch<FinanceSummary>(`/finance/summary${query ? `?${query}` : ''}`);
}

/**
 * Export financial data as CSV
 * Requirements: 16.7
 */
export async function exportFinanceCSV(params: {
  dateFrom: string;
  dateTo: string;
}): Promise<Blob> {
  const initData = getTelegramInitData();
  const devTelegramId = !initData ? getDevTelegramId() : undefined;
  if (!initData && !devTelegramId) {
    throw new Error('Missing Telegram initData');
  }
  const searchParams = new URLSearchParams({
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
  });

  const response = await fetch(
    `${API_BASE_URL}/finance/export?${searchParams.toString()}`,
    {
      headers: {
        ...(initData ? { 'X-Telegram-Init-Data': initData } : {}),
        ...(devTelegramId ? { 'X-Dev-Telegram-Id': devTelegramId } : {}),
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.blob();
}

// Dashboard Stats Types
export interface DashboardStats {
  ordersToday: number;
  ordersDelta: number;
  revenueToday: string;
  pendingMasters: number;
  activeOrders: number;
  onlineMasters: number;
  timestamp: string;
}

/**
 * Fetch dashboard KPI statistics
 */
export async function fetchStats(): Promise<DashboardStats> {
  return apiFetch<DashboardStats>('/stats');
}

// Event Feed Types
export type FeedEventAction =
  | 'MASTER_STATUS_CHANGED'
  | 'CONTACTS_REVEALED'
  | 'ORDER_DISPUTED'
  | 'ORDER_CREATED'
  | 'ORDER_STATUS_CHANGED'
  | 'PAYMENT_RECEIVED'
  | 'PAYMENT_REFUNDED'
  | 'BID_PLACED';

export interface FeedEvent {
  id: string;
  action: string;
  details: Record<string, unknown> | null;
  channel: string | null;
  orderId: string | null;
  user: {
    name: string;
    telegramId: string;
    role: string;
  } | null;
  createdAt: string;
}

export interface FeedResponse {
  data: FeedEvent[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

/**
 * Fetch event feed from AuditLog
 */
export async function fetchFeed(params?: {
  limit?: number;
  offset?: number;
}): Promise<FeedResponse> {
  const searchParams = new URLSearchParams();

  if (params?.limit) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params?.offset) {
    searchParams.set('offset', params.offset.toString());
  }

  const query = searchParams.toString();
  return apiFetch<FeedResponse>(`/feed${query ? `?${query}` : ''}`);
}
